export type TaskStatus =
  | "processing"
  | "completed"
  | "failed"
  | "cancelled"
  | "not_found";

export interface TaskMessage {
  status: TaskStatus;
  download_url?: string;
  error?: string;
}

export interface TaskCallbacks {
  onStatusChange: (statusText: string) => void;
  onSuccess: (downloadUrl: string) => void;
  onFinalize: () => void;
}

export class ReportTaskService {
  private ws: WebSocket | null = null;

  public downloadFile(downloadUrl: string): void {
    const link = document.createElement("a");
    link.href = downloadUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public connect(taskId: string, callbacks: TaskCallbacks): void {
    this.disconnect();

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/${taskId}`;

    this.ws = new WebSocket(wsUrl);

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const data: TaskMessage = JSON.parse(event.data);

        switch (data.status) {
          case "processing":
            callbacks.onStatusChange(
              "Generating report. This may take up to one minute, please wait…",
            );
            break;

          case "completed":
            callbacks.onStatusChange(
              "Report generated successfully! Downloading...",
            );
            if (data.download_url) {
              this.downloadFile(data.download_url);
            }
            callbacks.onSuccess(data.download_url || "");
            this.disconnect();
            callbacks.onFinalize();
            break;

          case "failed":
            callbacks.onStatusChange(
              `Generation failed: ${data.error || "Unknown error"}`,
            );
            this.disconnect();
            callbacks.onFinalize();
            break;

          case "cancelled":
            callbacks.onStatusChange("Report generation cancelled.");
            this.disconnect();
            callbacks.onFinalize();
            break;

          case "not_found":
            callbacks.onStatusChange("Task session not found on the server.");
            this.disconnect();
            callbacks.onFinalize();
            break;

          default:
            break;
        }
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
      }
    };

    this.ws.onerror = () => {
      callbacks.onStatusChange("WebSocket connection error.");
      this.disconnect();
      callbacks.onFinalize();
    };
  }

  public disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}
